import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import axios from "axios";
import { Loader2, FileText, AlertTriangle, CheckCircle, Phone, Mail, MapPin } from "lucide-react";
import PhotoLightbox from "../components/PhotoLightbox";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

const CONDITION_COLORS = {
  good: "bg-emerald-100 text-emerald-700",
  fair: "bg-amber-100 text-amber-700",
  poor: "bg-orange-100 text-orange-700",
  damaged: "bg-red-100 text-red-700",
};

const CONDITION_ICONS = {
  good: CheckCircle,
  fair: AlertTriangle,
  poor: AlertTriangle,
  damaged: AlertTriangle,
};

export default function PublicInspectionView() {
  const { reportId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API_URL}/public/inspections/${reportId}`);
        setData(res.data);
      } catch (err) {
        setError(err.response?.status === 404 ? "Report not found" : "Failed to load report");
      }
      setLoading(false);
    };
    load();
  }, [reportId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F4C81]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-slate-800 mb-2">{error}</h2>
            <p className="text-slate-500">Please check the link or contact the company.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { report, contact, company, photos } = data;
  const beforePhotos = photos.filter(p => p.tag === "before");
  const afterPhotos = photos.filter(p => p.tag === "after");
  const otherPhotos = photos.filter(p => !p.tag || (p.tag !== "before" && p.tag !== "after"));

  // Unified ordered list so the lightbox can swipe across all sections.
  const ordered = [...beforePhotos, ...afterPhotos, ...otherPhotos]
    .map(p => ({ ...p, url: p.file_url || p.url }));
  const indexOf = (p) => ordered.findIndex(o => o.id === p.id);

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-[#0F4C81] text-white py-4 px-6 shadow-lg">
        <div className="max-w-3xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-lg font-bold" data-testid="inspection-company-name">{company.name}</h1>
            <div className="flex items-center gap-4 text-sm text-blue-200 mt-1">
              {company.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</span>}
              {company.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</span>}
            </div>
          </div>
          <Badge className="bg-white/20 text-white border-white/30 capitalize">
            {report.report_type}
          </Badge>
        </div>
      </div>

      <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Report Info */}
        <Card>
          <CardContent className="p-6">
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold text-[#0F4C81]" data-testid="inspection-title">INSPECTION REPORT</h2>
              <p className="text-slate-500">Report #{report.report_number || "---"}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Prepared For</p>
                <p className="font-medium text-slate-800" data-testid="inspection-contact-name">{contact.name}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Property</p>
                {report.property_address && <p className="font-medium text-slate-800 flex items-center gap-1"><MapPin className="w-3 h-3" />{report.property_address}</p>}
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Inspector</p>
                <p className="font-medium text-slate-800">{report.inspector_name || "N/A"}</p>
              </div>
              <div>
                <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Date</p>
                <p className="font-medium text-slate-800">{report.inspection_date ? new Date(report.inspection_date).toLocaleDateString() : "N/A"}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Inspection Items */}
        {report.items?.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Inspection Findings</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-3">
                {report.items.map((item, i) => {
                  const CondIcon = CONDITION_ICONS[item.condition] || CheckCircle;
                  return (
                    <div key={i} className="p-4 bg-slate-50 rounded-lg" data-testid={`inspection-item-${i}`}>
                      <div className="flex items-center gap-3 mb-2">
                        <CondIcon className={`w-5 h-5 ${item.condition === "good" ? "text-emerald-500" : item.condition === "fair" ? "text-amber-500" : "text-red-500"}`} />
                        <p className="font-medium text-slate-700 flex-1">{item.area}</p>
                        <Badge className={CONDITION_COLORS[item.condition] || "bg-slate-100 text-slate-600"}>
                          {item.condition}
                        </Badge>
                      </div>
                      {item.notes && <p className="text-sm text-slate-600 ml-8">{item.notes}</p>}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Summary & Recommendations */}
        {(report.summary || report.recommendations) && (
          <Card>
            <CardContent className="p-6 space-y-4">
              {report.summary && (
                <div>
                  <p className="font-semibold text-slate-700 mb-2">Summary</p>
                  <p className="text-slate-600 whitespace-pre-wrap">{report.summary}</p>
                </div>
              )}
              {report.recommendations && (
                <div>
                  <p className="font-semibold text-slate-700 mb-2">Recommendations</p>
                  <p className="text-slate-600 whitespace-pre-wrap">{report.recommendations}</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Photos */}
        {photos.length > 0 && (
          <Card>
            <CardHeader><CardTitle>Photos</CardTitle></CardHeader>
            <CardContent>
              {/* Before/After */}
              {(beforePhotos.length > 0 || afterPhotos.length > 0) && (
                <div className="mb-6">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {beforePhotos.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-600 mb-2">Before</p>
                        <div className="space-y-2">
                          {beforePhotos.map((p) => (
                            <button key={p.id} type="button" onClick={() => setLightboxIndex(indexOf(p))} className="block w-full">
                              <img src={p.file_url || p.url} alt="Before" className="w-full rounded-lg border hover:opacity-90 transition-opacity" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    {afterPhotos.length > 0 && (
                      <div>
                        <p className="text-sm font-semibold text-slate-600 mb-2">After</p>
                        <div className="space-y-2">
                          {afterPhotos.map((p) => (
                            <button key={p.id} type="button" onClick={() => setLightboxIndex(indexOf(p))} className="block w-full">
                              <img src={p.file_url || p.url} alt="After" className="w-full rounded-lg border hover:opacity-90 transition-opacity" />
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
              {/* Other photos */}
              {otherPhotos.length > 0 && (
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {otherPhotos.map((p) => (
                    <button key={p.id} type="button" onClick={() => setLightboxIndex(indexOf(p))} className="block w-full">
                      <img src={p.file_url || p.url} alt="Inspection" className="w-full rounded-lg border hover:opacity-90 transition-opacity" />
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <PhotoLightbox
          photos={ordered}
          index={lightboxIndex}
          onClose={() => setLightboxIndex(null)}
          onIndexChange={setLightboxIndex}
        />

        {/* Footer */}
        <div className="text-center pb-8 text-sm text-slate-400">
          <p>{company.name}</p>
        </div>
      </div>
    </div>
  );
}
